import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
//import Turtle from 'drawing/util/Turtle';
import Turtle3D from 'drawing/util/Turtle3D';
import Instruction from 'entities/instruction/Instruction';
import ColorMap from './ColorMap';

import './geometry/Geometry';
// the same points turn the curve off by 45 degrees depending on which geometry we use. '
// It's not wrong from a results standpoint but it's weird and unexpected
//import ThreeGeometry from './geometry/ThreeGeometry';
import ThreeGeometry from './geometry/ThreeBufferGeometry';
import { Geometry } from './geometry/Geometry';

class ThreejsDrawingSystem {
    private turtles: Turtle3D[] = [new Turtle3D()];
    private scene: THREE.Scene;
    private renderer: THREE.Renderer;
    private camera: THREE.Camera;
    private controls: OrbitControls;

    private geometry: Geometry;

    constructor(scene: THREE.Scene, renderer: THREE.Renderer, camera: THREE.Camera, controls: OrbitControls) {
        this.scene = scene;
        this.renderer = renderer;
        this.camera = camera;
        this.controls = controls;

        this.geometry = new ThreeGeometry();
        this.geometry.pushLine('black', false);
        this.geometry.pushPoint(this._getTurtle().getPoint());
    }

    private _getTurtle(): Turtle3D {
        return this.turtles[this.turtles.length - 1];
    }

    setCamera(): void {
        this.controls.reset();
        this.camera.position.set(100, 0, 0);
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    clear(): void {
        this.geometry.clear(this.scene);

        this.turtles = [new Turtle3D()];
        this.geometry.pushLine('black', false);
        this.geometry.pushPoint(this._getTurtle().getPoint());
    }

    add(): void {
        this.geometry.add(this.scene);
    }

    render(): void {
        this.renderer.render(this.scene, this.camera);
    }

    getCanvas(): HTMLCanvasElement {
        return this.renderer.domElement;
    }

    turnLeft(by: number): void {
        this._getTurtle().turnX(by);
    }

    turnRight(by: number): void {
        this._getTurtle().turnX(-by);
    }

    pitchUp(by: number): void {
        this._getTurtle().turnY(by);
    }

    pitchDown(by: number): void {
        this._getTurtle().turnY(-by);
    }

    tiltLeft(by: number): void {
        this._getTurtle().turnZ(by);
    }

    tiltRight(by: number): void {
        this._getTurtle().turnZ(-by);
    }

    forward(by: number): void {
        this._getTurtle().moveY(by);
        const end = this._getTurtle().getPoint();
        this.geometry.pushPoint(end);
    }

    setColor(instruction: Instruction): void {
        const colorName = ColorMap[instruction]?.name;
        if (colorName) {
            this.geometry.pushLine(colorName, true);
        }
    }

    push(): void {
        this.turtles.push(this._getTurtle().clone());
    }

    pop(): void {
        this.turtles.pop();
        const color = this.geometry.getLineColor(this.turtles.length - 1) || 'black';

        this.geometry.pushLine(color, false);
        this.geometry.pushPoint(this._getTurtle().getPoint());
    }

    geometryForward(): void {
        this.geometry.forward();
    }
    geometryBackward(): void {
        this.geometry.backward();
    }
}

const axisHelper = (scene: THREE.Scene): void => {
    const origin = new THREE.Vector3(0, 0, 0);

    const makeLine = (x: number, y: number, z: number, color: number): THREE.Line => {
        const axis = [origin, new THREE.Vector3(x, y, z)];
        const geometry = new THREE.BufferGeometry().setFromPoints(axis);
        const material = new THREE.LineBasicMaterial({ color });

        return new THREE.Line(geometry, material);
    };

    const axisStep = 5;
    scene.add(makeLine(axisStep, 0, 0, 0xff0000)); //x-axis
    scene.add(makeLine(0, axisStep, 0, 0x00ff00)); //y-axis
    scene.add(makeLine(0, 0, axisStep, 0x0000ff)); //z-axis
};

const createFromSize = (width: number, height: number): ThreejsDrawingSystem => {
    // === THREE.JS CODE START ===
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer();
    renderer.setSize(width, height);

    axisHelper(scene);

    const controls = new OrbitControls(camera, renderer.domElement);
    const drawingSystem = new ThreejsDrawingSystem(scene, renderer, camera, controls);
    controls.addEventListener('change', () => drawingSystem.render());

    return drawingSystem;
};

export { createFromSize };
export default ThreejsDrawingSystem;
